import type { FC, ReactNode } from 'react'
import type { GetStaticProps } from 'next'
import type { Client } from '@/types'

import Image from 'next/image'
import { MapPin } from 'lucide-react'
import { safely } from '@/lib/db'
import { getClients } from '@/lib/content'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { PageHero } from '@/components/generals/page-hero'
import { CtaBanner } from '@/components/generals/cta-banner'
import { ClientsMarquee } from '@/components/generals/clients-marquee'
import { SectionHeading } from '@/components/generals/section-heading'
import { contactInfo, eventRegions } from '@/constants/site'

import {
    aboutImages,
    aboutValues,
    processSteps,
    venues
} from '@/constants/content'

interface AboutProps {
    clients: Client[]
}

const About: FC<AboutProps> = ({ clients }): ReactNode => {
    return (
        <Layout>
            <Seo
                title='About us'
                description='Meet Guju Events, the Ahmedabad team behind luxury weddings, corporate launches, Navratri nights, concerts and stadium manpower across India.'
            />

            <PageHero
                eyebrow='About us'
                title={(
                    <>
                        About
                        {' '}
                        <em className='gold-gradient-text pr-2'>Guju Events</em>
                    </>
                )}
                description='Crafting unforgettable experiences across India, from intimate family functions to stadium-scale productions.'
                image={aboutImages.story.src}
            />

            <section id='our-story' aria-labelledby='story-title' className='bg-ivory py-24 lg:py-32'>
                <div className='mx-auto grid max-w-7xl items-center gap-14 px-6 lg:grid-cols-2 lg:gap-20'>
                    <div className='relative aspect-[4/3] overflow-hidden rounded-[2rem] shadow-[0_40px_80px_-40px_rgba(27,18,25,0.55)] lg:order-2'>
                        <Image
                            src={aboutImages.story.src}
                            alt={aboutImages.story.alt}
                            fill
                            sizes='(min-width: 1024px) 50vw, 100vw'
                            className='object-cover'
                        />
                    </div>

                    <div>
                        <SectionHeading
                            eyebrow='Our story'
                            titleId='story-title'
                            title={(
                                <>
                                    We create luxury events that
                                    {' '}
                                    <em className='text-crimson'>inspire</em>
                                </>
                            )}
                        />

                        <div className='mt-7 space-y-5 text-base leading-relaxed text-muted-foreground sm:text-lg'>
                            <p>
                                Guju Events is a premium event management company delivering world-class weddings, corporate events, exhibitions and large-scale entertainment experiences across India.
                            </p>

                            <p>
                                With expertise in handling high-budget productions and large audiences, we combine creativity, precision and execution excellence to deliver flawless results.
                            </p>

                            <p>
                                From Ahmedabad to Mumbai and Delhi, we have earned a reputation for reliability, luxury presentation and powerful brand execution.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            <section id='values' aria-labelledby='values-title' className='bg-sand py-24 lg:py-28'>
                <div className='mx-auto max-w-7xl px-6'>
                    <SectionHeading
                        eyebrow='What drives us'
                        titleId='values-title'
                        align='center'
                        title={(
                            <>
                                Why clients
                                {' '}
                                <em className='text-crimson'>come back</em>
                            </>
                        )}
                    />

                    <div className='mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4'>
                        {aboutValues.map(({ icon: Icon, title, text }) => (
                            <div key={title} className='rounded-3xl border border-ink/8 bg-white p-7'>
                                <span className='inline-flex size-12 items-center justify-center rounded-2xl bg-crimson/10 text-crimson'>
                                    <Icon className='size-5' aria-hidden='true' />
                                </span>

                                <h3 className='mt-6 font-heading text-2xl text-ink'>
                                    {title}
                                </h3>

                                <p className='mt-3 text-sm leading-relaxed text-muted-foreground'>
                                    {text}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section id='where-we-work' aria-labelledby='venues-title' className='surface-night py-24 text-white lg:py-32'>
                <div className='mx-auto max-w-7xl px-6'>
                    <SectionHeading
                        tone='dark'
                        eyebrow='Where we have worked'
                        titleId='venues-title'
                        title={(
                            <>
                                Big stages,
                                {' '}
                                <em className='text-gold'>bigger crowds</em>
                            </>
                        )}
                        description='Some of the venues and shows our teams have been part of.'
                    />

                    <div className='mt-14 grid gap-5 md:grid-cols-3'>
                        {venues.map((venue) => (
                            <figure key={venue.title} className='group relative aspect-[4/5] overflow-hidden rounded-[1.75rem] border border-white/10'>
                                <Image
                                    src={venue.image}
                                    alt={venue.alt}
                                    fill
                                    sizes='(min-width: 768px) 33vw, 100vw'
                                    className='object-cover transition-transform duration-700 group-hover:scale-[1.04]'
                                />

                                <div aria-hidden='true' className='absolute inset-0 bg-linear-to-t from-night via-night/30 to-transparent' />

                                <figcaption className='absolute inset-x-0 bottom-0 p-6'>
                                    <p className='font-heading text-2xl leading-tight'>
                                        {venue.title}
                                    </p>

                                    <p className='mt-2 text-sm text-white/70'>
                                        {venue.text}
                                    </p>
                                </figcaption>
                            </figure>
                        ))}
                    </div>

                    <ol className='mt-20 grid gap-8 border-t border-white/10 pt-12 sm:grid-cols-2 lg:grid-cols-4'>
                        {processSteps.map((step, index) => (
                            <li key={step.title}>
                                <span className='font-heading text-5xl text-gold'>
                                    {String(index + 1).padStart(2, '0')}
                                </span>

                                <h3 className='mt-4 text-lg font-semibold'>
                                    {step.title}
                                </h3>

                                <p className='mt-2 text-sm leading-relaxed text-white/60'>
                                    {step.text}
                                </p>
                            </li>
                        ))}
                    </ol>
                </div>
            </section>

            <section id='major-events' aria-labelledby='regions-title' className='bg-ivory py-24 lg:py-28'>
                <div className='mx-auto grid max-w-7xl gap-12 px-6 lg:grid-cols-12 lg:items-center'>
                    <div className='lg:col-span-5'>
                        <SectionHeading
                            eyebrow='Our major events'
                            titleId='regions-title'
                            title={(
                                <>
                                    Rooted in Ahmedabad,
                                    {' '}
                                    <em className='text-crimson'>working across India</em>
                                </>
                            )}
                            description={`Our base is in ${contactInfo.addressShort}. From there we take our teams wherever your event is.`}
                        />
                    </div>

                    <ul className='grid grid-cols-2 gap-4 sm:grid-cols-3 lg:col-span-7'>
                        {eventRegions.map((region) => (
                            <li key={region} className='flex items-center gap-3 rounded-2xl border border-ink/8 bg-white px-5 py-6'>
                                <MapPin className='size-5 shrink-0 text-crimson' aria-hidden='true' />
                                <span className='font-heading text-2xl text-ink'>
                                    {region}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            <ClientsMarquee clients={clients} />
            <CtaBanner />
        </Layout>
    )
}

export const getStaticProps: GetStaticProps<AboutProps> = async () => {
    const clients = await safely('about clients', () => getClients(), [])

    return {
        props: { clients },
        revalidate: 60
    }
}

export default About
